import React, { useContext, useState, useMemo } from 'react';
import { MachineGroupContext } from '../DataContexts/MachineGroupContext';
import { MachineContext } from '../DataContexts/MachineContext';
import './MachineGroups.css';

// DJB-172: the machine tables in the Groups section share these columns
// (postal code is new) and sort by clicking a header — same ▲/▼ affordance
// as the Accounts Overview table.
const MACHINE_TABLE_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'postalcode', label: 'Postal Code' },
  { key: 'city', label: 'City' },
  { key: 'province', label: 'Province' },
];

// numeric: true so postal codes ("1000" < "9990") and numbered names order
// naturally instead of lexically
const sortMachines = (machines, { key, direction }) => {
  if (!key) return machines;
  const sorted = [...machines].sort((a, b) =>
    String(a[key] ?? '').localeCompare(String(b[key] ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  );
  return direction === 'desc' ? sorted.reverse() : sorted;
};

const MachineTableHeaders = ({ sort, onSort, children }) => (
  <tr>
    {MACHINE_TABLE_COLUMNS.map(({ key, label }) => (
      <th
        key={key}
        className="mg-sortable-header"
        aria-sort={
          sort.key === key
            ? sort.direction === 'asc'
              ? 'ascending'
              : 'descending'
            : undefined
        }
        onClick={() => onSort(key)}
      >
        {label}
        {sort.key === key ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : ''}
      </th>
    ))}
    {children}
  </tr>
);

const MachineGroups = () => {
  const { 
    machineGroups, 
    loading: groupsLoading, 
    error: groupsError, 
    createMachineGroup, 
    updateMachineGroup, 
    deleteMachineGroup,
    addMachineToGroup,
    removeMachineFromGroup
  } = useContext(MachineGroupContext);
  const { machines, loading: machinesLoading, error: machinesError } = useContext(MachineContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedMachines, setSelectedMachines] = useState([]); // Machines selected for intermediate table
  const [machineSearchQuery, setMachineSearchQuery] = useState('');
  const [machinesMarkedForRemoval, setMachinesMarkedForRemoval] = useState([]);
  // DJB-172: each table sorts independently
  const [groupSort, setGroupSort] = useState({ key: null, direction: 'asc' });
  const [selectedSort, setSelectedSort] = useState({ key: null, direction: 'asc' });
  const [availableSort, setAvailableSort] = useState({ key: null, direction: 'asc' });

  const makeRequestSort = (setSort) => (key) =>
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

  // Filter machine groups based on search query
  const filteredGroups = useMemo(() => {
    if (!machineGroups) return [];
    return machineGroups.filter((group) =>
      group.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [machineGroups, searchQuery]);

  // Filter available machines (not in the selected group)
  const filteredAvailableMachines = useMemo(() => {
    if (!selectedGroup || !machines) return [];
    const query = machineSearchQuery.toLowerCase();
    return machines.filter(machine =>
      !selectedGroup.machineIds?.includes(machine.id) &&
      [machine.name, machine.city, machine.province, machine.postalcode]
        .some(field => String(field ?? '').toLowerCase().includes(query))
    );
  }, [machines, selectedGroup, machineSearchQuery]);

  // Get machines in the selected group
  const groupMachines = useMemo(() => {
    if (!selectedGroup || !machines) return [];
    return machines.filter(machine =>
      selectedGroup.machineIds?.includes(machine.id)
    );
  }, [machines, selectedGroup]);

  // DJB-172: sorted views of the three tables
  const sortedGroupMachines = useMemo(
    () => sortMachines(groupMachines, groupSort),
    [groupMachines, groupSort]
  );
  const sortedSelectedMachines = useMemo(
    () => sortMachines(selectedMachines, selectedSort),
    [selectedMachines, selectedSort]
  );
  const sortedAvailableMachines = useMemo(
    () => sortMachines(filteredAvailableMachines, availableSort),
    [filteredAvailableMachines, availableSort]
  );

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      alert('Please enter a group name.');
      return;
    }

    const newGroup = {
      name: newGroupName.trim(),
      machines: []
    };

    try {
      await createMachineGroup(newGroup);
      setNewGroupName('');
    } catch (error) {
      console.error('Failed to create machine group:', error);
    }
  };

  // Handle editing a group
  const handleEditClick = () => {
    setIsEditing(true);
    setEditName(selectedGroup.name);
  };

  const handleSaveClick = async () => {
    try {
      // Only send the name in the update request
      await updateMachineGroup({
        id: selectedGroup.id,
        name: editName
      });
      
      // Update the local state with the full group data but new name
      const updatedGroup = {
        ...selectedGroup,
        name: editName
      };
      setSelectedGroup(updatedGroup);
      setMachinesMarkedForRemoval([]);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save machine group:', error);
    }
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditName(selectedGroup.name);
    setMachinesMarkedForRemoval([]);
  };

  // Handle machine selection for intermediate table
  const handleAddMachineToIntermediate = (machine) => {
    if (!selectedMachines.find(selected => selected.id === machine.id)) {
      setSelectedMachines([...selectedMachines, machine]);
    }
  };

  const handleRemoveFromIntermediate = (machineId) => {
    setSelectedMachines(prevMachines =>
      prevMachines.filter(machine => machine.id !== machineId)
    );
  };

  const handleAddAllToIntermediate = () => {
    const newMachines = filteredAvailableMachines.filter(
      machine => !selectedMachines.some(selected => selected.id === machine.id)
    );
    setSelectedMachines([...selectedMachines, ...newMachines]);
  };

  // Handle adding machines to the group
  const handleAddToGroup = async () => {
    if (selectedGroup && selectedMachines.length > 0) {
      try {
        // Add each machine individually using the correct endpoint
        for (const machine of selectedMachines) {
          await addMachineToGroup(machine.id, selectedGroup.id);
        }

        // Update local state after all machines are added
        const updatedGroup = {
          ...selectedGroup,
          machineIds: [...(selectedGroup.machineIds || []), ...selectedMachines.map(machine => machine.id)]
        };
        setSelectedGroup(updatedGroup);
        setSelectedMachines([]); // Clear the intermediate table
      } catch (error) {
        console.error('Failed to add machines to group:', error);
      }
    }
  };

  // Handle removing machines from the group
  const handleRemoveFromGroup = async (machineId) => {
    if (machinesMarkedForRemoval.includes(machineId)) {
      // Unmark the machine for removal
      setMachinesMarkedForRemoval(machinesMarkedForRemoval.filter(id => id !== machineId));
    } else {
      try {
        // Remove the machine from the group using the correct endpoint
        await removeMachineFromGroup(machineId, selectedGroup.id);

        // Update local state
        const updatedGroup = {
          ...selectedGroup,
          machineIds: selectedGroup.machineIds?.filter(id => id !== machineId) || []
        };
        setSelectedGroup(updatedGroup);
        setMachinesMarkedForRemoval([...machinesMarkedForRemoval, machineId]);
      } catch (error) {
        console.error('Failed to remove machine from group:', error);
      }
    }
  };

  // Handle deleting a group
  const handleDeleteGroup = async () => {
    if (selectedGroup) {
      const confirmDelete = window.confirm(
        `Are you sure you want to delete the machine group "${selectedGroup.name}"?`
      );
      if (confirmDelete) {
        await deleteMachineGroup(selectedGroup.id);
        setSelectedGroup(null);
      }
    }
  };

  if (groupsLoading || machinesLoading) return <div className="loading-message">Loading...</div>;
  if (groupsError) return <div className="error-message">Error loading groups: {groupsError}</div>;
  if (machinesError) return <div className="error-message">Error loading machines: {machinesError}</div>;

  return (
    <div className="machine-groups">
      {/* Create Group Form */}
      <div className="create-group-form">
        <div className="create-group-input-container">
          <input
            type="text"
            placeholder="Enter new group name..."
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            className="create-group-input"
          />
          <button
            onClick={handleCreateGroup}
            className="create-group-button"
            disabled={!newGroupName.trim()}
          >
            Create Group
          </button>
        </div>
      </div>

      {/* DJB-171: mg-* layout classes are scoped to this page — the legacy
          global .columns/.column rules (still shared by the other manager
          pages) no longer apply here */}
      <div className="mg-columns">
        {/* Left column - Machine Groups list */}
        <div className="mg-column mg-column--groups">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search machine groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-box"
            />
          </div>
          <ul className="group-list-content">
            {filteredGroups.map((group) => (
              <li
                key={group.id}
                className={selectedGroup?.id === group.id ? 'selected' : ''}
                onClick={() => {
                  setSelectedGroup(group);
                  setSelectedMachines([]);
                  setIsEditing(false);
                }}
              >
                <span className="group-name">{group.name}</span>
                <span className="group-machine-count">
                  {group.machineIds?.length || 0} machines
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Middle column - Group details */}
        <div className="mg-column mg-column--details">
          {selectedGroup ? (
            <div className="group-details">
              <div className="group-header">
                <div className="group-info">
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="edit-input"
                      />
                      <div className="group-actions">
                        <button className="edit-button" onClick={handleSaveClick}>
                          Save
                        </button>
                        <button className="cancel-button" onClick={handleCancelClick}>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3>{selectedGroup.name}</h3>
                      <div className="group-actions">
                        <button className="edit-button" onClick={handleEditClick}>
                          Edit
                        </button>
                        <button className="delete-button" onClick={handleDeleteGroup}>
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Machines in Group */}
              <div className="group-header">
                <h5>Machines in Group: {groupMachines.length}</h5>
              </div>
              <table className="mg-table">
                <thead>
                  <MachineTableHeaders
                    sort={groupSort}
                    onSort={makeRequestSort(setGroupSort)}
                  >
                    {isEditing && <th></th>}
                  </MachineTableHeaders>
                </thead>
                <tbody>
                  {sortedGroupMachines.map((machine) => (
                    <tr
                      key={machine.id}
                      className={
                        machinesMarkedForRemoval.includes(machine.id)
                          ? 'marked-for-removal'
                          : ''
                      }
                    >
                      <td>{machine.name}</td>
                      <td>{machine.postalcode || '-'}</td>
                      <td>{machine.city || '-'}</td>
                      <td>{machine.province || '-'}</td>
                      {isEditing && (
                        <td>
                          <button
                            className={machinesMarkedForRemoval.includes(machine.id)
                              ? 'add-button'
                              : 'remove-button'}
                            onClick={() => handleRemoveFromGroup(machine.id)}
                          >
                            {machinesMarkedForRemoval.includes(machine.id) ? '+' : '-'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>Select a machine group to view details.</p>
          )}
        </div>

        {/* Right column - Machine selection */}
        <div className="mg-column mg-column--picker">
          {!selectedGroup ? (
            <p>Select a machine group to add machines.</p>
          ) : (
            <>
              {/* Intermediate Table */}
              {selectedMachines.length > 0 && (
                <div className="intermediate-table">
                  <h5>Selected Machines</h5>
                  <table className="mg-table">
                    <thead>
                      <MachineTableHeaders
                        sort={selectedSort}
                        onSort={makeRequestSort(setSelectedSort)}
                      >
                        <th></th>
                      </MachineTableHeaders>
                    </thead>
                    <tbody>
                      {sortedSelectedMachines.map((machine) => (
                        <tr key={machine.id}>
                          <td>{machine.name}</td>
                          <td>{machine.postalcode || '-'}</td>
                          <td>{machine.city || '-'}</td>
                          <td>{machine.province || '-'}</td>
                          <td>
                            <button
                              className="remove-button"
                              onClick={() => handleRemoveFromIntermediate(machine.id)}
                            >
                              X
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="intermediate-actions">
                    <button className="edit-button" onClick={handleAddToGroup}>
                      Add to Current Group
                    </button>
                    <button
                      className="cancel-button"
                      onClick={() => setSelectedMachines([])}
                    >
                      Remove All
                    </button>
                  </div>
                </div>
              )}

              {/* Available Machines */}
              <input
                type="text"
                placeholder="Search by name, city, postal code or province..."
                className="search-box"
                value={machineSearchQuery}
                onChange={(e) => setMachineSearchQuery(e.target.value)}
              />
              <table className="mg-table">
                <thead>
                  <MachineTableHeaders
                    sort={availableSort}
                    onSort={makeRequestSort(setAvailableSort)}
                  >
                    <th>
                      <button
                        className="add-button"
                        onClick={handleAddAllToIntermediate}
                      >
                        Add All
                      </button>
                    </th>
                  </MachineTableHeaders>
                </thead>
                <tbody>
                  {sortedAvailableMachines.map((machine) => (
                    <tr key={machine.id}>
                      <td>{machine.name}</td>
                      <td>{machine.postalcode || '-'}</td>
                      <td>{machine.city || '-'}</td>
                      <td>{machine.province || '-'}</td>
                      <td>
                        <button
                          className="add-button"
                          onClick={() => handleAddMachineToIntermediate(machine)}
                        >
                          +
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MachineGroups;
